import React, { useEffect, useState } from "react";
import { Theme, useTheme } from "@mui/material/styles";
import { Grid, Typography, useMediaQuery } from "@mui/material";
import "../../assets/css/Blog/BlogDetail.css";
import {
  KeyboardDoubleArrowLeftOutlined,
  KeyboardDoubleArrowRightOutlined,
} from "@mui/icons-material";
import parse from "html-react-parser";
import CtaCompany from "../Company/CtaCompany";
import CtaShareTransfer from "../ShareTransfer/CtaShareTransfer";
import CtaTrademark from "../Trademark/CtaTrademark";
import Footer from "../Footer/Footer";
import axios, { AxiosResponse } from "axios";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

interface BlogDetailProps {}

export interface Article {
  id?: string;
  title?: string;
  author?: string;
  summary?: string;
  published?: boolean;
  content?: string;
  createdAt?: number;
  updatedAt?: number;
  image_base64?: string;
  meta?: string;
  type?: string;
  imgPreview?: string;
}

const BlogDetail: React.FC<BlogDetailProps> = ({}) => {
  const theme = useTheme<Theme>();
  const md = useMediaQuery(theme.breakpoints.up("md"));

  const [blogData, setBlogData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [article, setArticle] = useState<Article | null>(null);

  const [previousArticle, setPreviousArticle] = useState<Article | null>(null);
  const [nextArticle, setNextArticle] = useState<Article | null>(null);

  useEffect(() => {
    document.body.setAttribute("data-root", window.location.pathname);

    const urlSlug = window.location.pathname.replace("/b/", " ");

    const fetchPublishedArticles = async () => {
      try {
        let baseUrl = "https://api.legalfarm.it";

        if (window.location.href.includes("https://www.legalfarm.it/")) {
          baseUrl = "https://api.legalfarm.it";
        }

        const trimmedUrlSlug = urlSlug.trim();

        const url = `${baseUrl}/blog/meta?slug=${trimmedUrlSlug}`;

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Errore nella richiesta: ${response.status}`);
        }

        const data = await response.json();
        const fetchedArticle = data.post;

        if (fetchedArticle.image_base64) {
          const imgPreview = await getImgCdn(fetchedArticle.image_base64);
          fetchedArticle.imgPreview = imgPreview;
        }

        setArticle(fetchedArticle);
      } catch (error) {
        console.error(error);
      }
    };

    fetchPublishedArticles();

    return () => {
      document.body.removeAttribute("data-root");
    };
  }, []);

  const getImgCdn = async (id: string) => {
    try {
      let baseUrl = "https://api.legalfarm.it";

      if (window.location.href.includes("https://www.legalfarm.it/")) {
        baseUrl = "https://api.legalfarm.it";
      }

      const url = `${baseUrl}`;
      const response: AxiosResponse<any> = await axios.get(
        `${url}/blog_cdn/${id}`
      );

      if (response.status === 200) {
        return response.data.URL;
      } else {
        console.error(response.statusText);
        return "";
      }
    } catch (error) {
      console.error(error);
      return "";
    }
  };

  const fetchPublishedArticles = async () => {
    try {
      setLoading(true);
      let baseUrl = "https://api.legalfarm.it";

      if (window.location.href.includes("https://www.legalfarm.it/")) {
        baseUrl = "https://api.legalfarm.it";
      }

      const url = `${baseUrl}/blog?published=true`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Errore nella richiesta: ${response.status}`);
      }
      const data = await response.json();
      const posts = data.posts.BlogPosts;

      const updatedPosts = await Promise.all(
        posts.map(async (post: any) => {
          const imageUrl = await getImgCdn(post.image_small);

          return { ...post, imgPreview: imageUrl };
        })
      );

      setBlogData(updatedPosts);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublishedArticles();
  }, []);

  useEffect(() => {
    if (article && blogData.length > 0) {
      // Ordina gli articoli per updatedAt
      const sortedBlogData = [...blogData].sort(
        (a, b) =>
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
      );

      // Trova l'indice dell'articolo corrente
      const currentIndex = sortedBlogData.findIndex(
        (item) => item.id === article.id
      );

      // Ottieni l'articolo precedente e successivo
      if (currentIndex !== -1) {
        const prevArticle =
          currentIndex > 0 ? sortedBlogData[currentIndex - 1] : null;
        const nextArticle =
          currentIndex < sortedBlogData.length - 1
            ? sortedBlogData[currentIndex + 1]
            : null;

        setPreviousArticle(prevArticle);
        setNextArticle(nextArticle);
      }
    }
  }, [article, blogData]);

  const articleNext = () => {
    if (nextArticle && nextArticle.meta) {
      window.location.href = `/b/${nextArticle.meta}`;
    }
  };

  const articlePrevious = () => {
    if (previousArticle && previousArticle.meta) {
      window.location.href = `/b/${previousArticle.meta}`;
    }
  };

  const capitalizeTitle = (title: string) => {
    return title
      .split(" ")
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <>
      <Helmet>
        <title>
          {article?.title
            ? `${capitalizeTitle(article?.title)} - LegalFarm`
            : "LegalFarm"}
        </title>
      </Helmet>

      {article ? (
        <>
          <Grid container style={{ display: "flex", justifyContent: "center" }}>
            <Grid
              item
              xs={10}
              style={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                alignItems: "center",
                marginTop: "50px",
                marginBottom: "50px",
              }}
            >
              {article && (
                <>
                  <Typography className="color-primary title-article-blog">
                    {article.title}
                  </Typography>
                </>
              )}

              <div className="container-img-article-blog">
                {article && article.imgPreview && (
                  <img
                    className="img-article-blog"
                    src={article.imgPreview}
                    alt={article.meta || "Immagine articolo"}
                  />
                )}
              </div>

              <div
                style={{
                  paddingTop: "50px",
                  width: "80%",
                  margin: "0 auto",
                }}
              >
                {article && (
                  <>
                    <Typography className="color-primary content">
                      {parse(article.content!)}
                    </Typography>
                  </>
                )}
              </div>
            </Grid>

            {/* Gestione pulsanti */}
            <Grid
              item
              xs={8}
              className="container-button-article-details"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "30px",
                marginBottom: "50px",
              }}
            >
              {/* Indietro */}
              <Grid
                item
                xs={2}
                sm={4}
                onClick={previousArticle ? articlePrevious : () => {}}
                style={{
                  opacity: previousArticle ? 1 : 0.5,
                  cursor: previousArticle ? "pointer" : "not-allowed",
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                <div className="container-previous-article">
                  <Typography
                    className="color-yellow"
                    style={{
                      textDecoration: previousArticle ? "underline" : "none",
                    }}
                  >
                    <KeyboardDoubleArrowLeftOutlined
                      style={{ fontSize: "16px", marginRight: "4px" }}
                    />
                    Articolo precedente
                  </Typography>
                </div>
                <div className="container-previous-article-icon">
                  <Typography
                    className="color-yellow"
                    style={{
                      textDecoration: previousArticle ? "underline" : "none",
                    }}
                  >
                    <KeyboardDoubleArrowLeftOutlined className="icon-previous" />
                  </Typography>
                </div>
              </Grid>

              {/* Vedi Tutti */}
              <Grid
                item
                xs={8}
                sm={4}
                style={{ display: "flex", justifyContent: "center" }}
              >
                <a
                  className="text-all-article"
                  href={`/b`}
                  style={{ cursor: "pointer" }}
                >
                  Tutti gli articoli
                </a>
              </Grid>

              {/* Avanti */}
              <Grid
                item
                xs={2}
                sm={4}
                onClick={nextArticle ? articleNext : () => {}}
                style={{
                  opacity: nextArticle ? 1 : 0.5,
                  cursor: nextArticle ? "pointer" : "not-allowed",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <div className="container-next-article">
                  <Typography
                    className="color-yellow"
                    style={{
                      textDecoration: nextArticle ? "underline" : "none",
                    }}
                  >
                    Articolo successivo
                    <KeyboardDoubleArrowRightOutlined
                      style={{
                        fontSize: "16px",
                        marginLeft: "4px",
                      }}
                    />
                  </Typography>
                </div>
                <div className="container-next-article-icon">
                  <Typography
                    className="color-yellow"
                    style={{
                      textDecoration: nextArticle ? "underline" : "none",
                    }}
                  >
                    <KeyboardDoubleArrowRightOutlined className="icon-next" />
                  </Typography>
                </div>
              </Grid>
            </Grid>

            {article && article.type === "Srl" ? (
              <CtaCompany ctaBlog={true} />
            ) : article && article.type === "Cessione" ? (
              <CtaShareTransfer ctaBlog={true} />
            ) : article && article.type === "Marchio" ? (
              <CtaTrademark ctaBlog={true} />
            ) : null}
          </Grid>
          <Footer />
        </>
      ) : null}
    </>
  );
};

export default BlogDetail;
